/**
 * Shared PDF generator for the 5 onboarding documents.
 *
 * The custom elements `netz-pdf-onboarding-*` are registered by the chancennetz print build; this module
 * only maps the 5 logical "kinds" to tag/filename/title and drives `createPDFBufferFromComponent`.
 * Keeping the mapping in `src/` (rather than in `chancennetz/src/`) allows `Mitarbeiter.completeOnboarding`
 * to attach the PDFs to the confirmation email without creating a cross-package dependency.
 */

import { createPDFBufferFromComponent } from '@adornis/print/server/print-internals.js';
import type { PDFOptions } from 'puppeteer';

export const ONBOARDING_PDF_KINDS = ['vertrag', 'datenschutz', 'selbstverpflichtung', 'masernimpfung'] as const;
export type OnboardingPdfKind = (typeof ONBOARDING_PDF_KINDS)[number];

const componentTagByKind: Record<OnboardingPdfKind, string> = {
  vertrag: 'netz-pdf-onboarding-vertrag',
  datenschutz: 'netz-pdf-onboarding-datenschutz',
  selbstverpflichtung: 'netz-pdf-onboarding-selbstverpflichtung',
  masernimpfung: 'netz-pdf-onboarding-masernimpfung',
};

export const onboardingPdfFileName: Record<OnboardingPdfKind, string> = {
  vertrag: 'Vereinbarung-ehrenamtliche-Taetigkeit.pdf',
  datenschutz: 'Datenschutzverpflichtung.pdf',
  selbstverpflichtung: 'Selbstverpflichtungserklaerung.pdf',
  masernimpfung: 'Einwilligung-Masernimpfung.pdf',
};

export const onboardingPdfTitle: Record<OnboardingPdfKind, string> = {
  vertrag: 'Vereinbarung über die Übernahme einer ehrenamtlichen Tätigkeit',
  datenschutz: 'Verpflichtung zur Einhaltung der DS-GVO',
  selbstverpflichtung: 'Selbstverpflichtungserklärung',
  masernimpfung: 'Einwilligung zur Weitergabe des Masern-Impfnachweises',
};

export interface OnboardingPdfAuth {
  /** `_id` of the Mitarbeiter the PDFs should be rendered for. */
  mitarbeiterID: string;
  /** Signed JWT accepted by `Mitarbeiter.getMitarbeiterWithOneTimeToken` / `Taetigkeit.getTaetigkeitenWithOneTimeToken`. */
  token: string;
}

/**
 * Puppeteer PDFOptions used when rendering the onboarding PDFs (header/footer templates, margins, etc.).
 * Registered from `chancennetz` startup via `setOnboardingPdfOptions` so that the templates can use
 * package-local assets (e.g. the logo data URI) without creating a cross-package dependency here.
 */
let _onboardingPdfOptions: PDFOptions | undefined;

export const setOnboardingPdfOptions = (opts: PDFOptions) => {
  _onboardingPdfOptions = opts;
};

/**
 * Generate a single onboarding PDF buffer. The print component runs in a detached puppeteer browser, so
 * it cannot rely on session/currentUser — instead we pass the Mitarbeiter id + a one-time token as query
 * params, mirroring the working `chancen-pdf-student-data` pattern.
 */
export async function createOnboardingPdfBuffer(kind: OnboardingPdfKind, auth: OnboardingPdfAuth): Promise<Uint8Array> {
  return createPDFBufferFromComponent({
    component: componentTagByKind[kind],
    queryParams: new URLSearchParams({ mitarbeiterID: auth.mitarbeiterID, token: auth.token }).toString(),
    timeout: 30_000,
    options: _onboardingPdfOptions,
  });
}

/** Generate all 5 PDFs in parallel; returns nodemailer-compatible `{ filename, content }` entries. */
export async function createAllOnboardingPdfAttachments(
  auth: OnboardingPdfAuth,
): Promise<Array<{ filename: string; content: Buffer }>> {
  const buffers = await Promise.all(ONBOARDING_PDF_KINDS.map(kind => createOnboardingPdfBuffer(kind, auth)));
  return ONBOARDING_PDF_KINDS.map((kind, i) => ({
    filename: onboardingPdfFileName[kind],
    content: Buffer.from(buffers[i]!),
  }));
}
